import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ClipboardList, RefreshCw } from "lucide-react";

export const ActivityLogTab = ({
  darkMode, activityLogs, activityTotal, activityPage,
  fetchActivityLog, isSuperAdmin,
  activityFilterEmail, setActivityFilterEmail,
  activityFilterAction, setActivityFilterAction,
  activityFilterOptions
}) => {
  const actionColors = {
    created_product: "bg-green-100 text-green-700",
    updated_product: "bg-blue-100 text-blue-700",
    deleted_product: "bg-red-100 text-red-700",
    created_promo_code: "bg-amber-100 text-amber-700",
    deleted_promo_code: "bg-red-100 text-red-700",
    created_banner: "bg-purple-100 text-purple-700",
    created_sub_admin: "bg-green-100 text-green-700",
    updated_sub_admin: "bg-blue-100 text-blue-700",
    removed_sub_admin: "bg-red-100 text-red-700",
    updated_api_key: "bg-orange-100 text-orange-700",
  };

  return (
    <Card className={`border-0 shadow-soft rounded-3xl ${darkMode ? "bg-gray-800" : ""}`}>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}>
          <ClipboardList className={`w-5 h-5 ${darkMode ? "text-blue-400" : "text-primary"}`} /> Activity Log
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          {isSuperAdmin && activityFilterOptions.emails.length > 0 && (
            <select
              className={`text-sm rounded-xl px-3 py-1.5 border ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200" : "bg-white border-gray-200"}`}
              value={activityFilterEmail}
              onChange={(e) => { setActivityFilterEmail(e.target.value); setTimeout(() => fetchActivityLog(1), 100); }}
              data-testid="activity-filter-email"
            >
              <option value="">All Users</option>
              {activityFilterOptions.emails.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          )}
          {activityFilterOptions.actions.length > 0 && (
            <select
              className={`text-sm rounded-xl px-3 py-1.5 border ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200" : "bg-white border-gray-200"}`}
              value={activityFilterAction}
              onChange={(e) => { setActivityFilterAction(e.target.value); setTimeout(() => fetchActivityLog(1), 100); }}
              data-testid="activity-filter-action"
            >
              <option value="">All Actions</option>
              {activityFilterOptions.actions.map(a => <option key={a} value={a}>{a.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</option>)}
            </select>
          )}
          <Button variant="outline" size="sm" className={`rounded-xl ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600" : ""}`} onClick={() => fetchActivityLog(1)} data-testid="refresh-activity-log">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {activityLogs.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList className={`w-12 h-12 mx-auto mb-3 ${darkMode ? "text-gray-600" : "text-gray-300"}`} />
            <p className={darkMode ? "text-gray-400" : "text-gray-500"}>No activity recorded yet</p>
            <p className={`text-sm ${darkMode ? "text-gray-500" : "text-gray-400"}`}>Admin actions will appear here as they happen</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activityLogs.map((log) => {
              const badgeClass = actionColors[log.action] || "bg-gray-100 text-gray-700";
              return (
                <div key={log.id} className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${darkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"}`} data-testid={`activity-log-${log.id}`}>
                  <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${log.action?.includes("delete") || log.action?.includes("remove") ? "bg-red-500" : log.action?.includes("create") || log.action?.includes("invite") ? "bg-green-500" : "bg-blue-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium text-sm ${darkMode ? "text-gray-200" : "text-gray-900"}`}>{log.user_name || log.user_email}</span>
                      <Badge className={`text-xs rounded-lg ${darkMode ? "bg-gray-700 text-gray-300 border-gray-600" : badgeClass} border-0`}>
                        {log.action?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                    </div>
                    {log.details && <p className={`text-sm mt-0.5 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{log.details}</p>}
                    <p className={`text-xs mt-1 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                      {new Date(log.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
            {/* Pagination */}
            {activityTotal > 30 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <Button variant="outline" size="sm" className={`rounded-xl ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600" : ""}`} disabled={activityPage <= 1} onClick={() => fetchActivityLog(activityPage - 1)}>
                  Previous
                </Button>
                <span className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Page {activityPage}</span>
                <Button variant="outline" size="sm" className={`rounded-xl ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600" : ""}`} disabled={activityPage * 30 >= activityTotal} onClick={() => fetchActivityLog(activityPage + 1)}>
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
